import { Address } from '@msdyn365-commerce/retail-proxy';
import { IActionContext } from '@msdyn365-commerce/core';
import getStoreLocationCentiroAction, {
    GetStoreLocationCentiroInput,
    IFullOrgUnitAvailabilityExtended
} from '../../actions/get-store-location-centiro.action';

import { IStoreSelectorLocation } from '@msdyn365-commerce-modules/bopis-utilities';
import { IMapWindow } from '@msdyn365-commerce-modules/bopis-utilities/src/store-selector-state-manager/models/map-data';
import { _readFromSessionStorage, _writeToSessionStorage } from '../../utils/helper';
import { createAzureMapsInstance } from './azure-maps';

const CENTIRO_STORES: string = 'bsCentiroStores';
export const COLLECTION_POINT_EXPIRATION_DAYS: number = 1;

export const loadCentiroStores = async (address: Address = {}, context: IActionContext): Promise<IFullOrgUnitAvailabilityExtended[]> => {
    let network = 'postnord_DK';
    const networkMapping = context.requestContext.app.config.networkMapping;
    if (networkMapping) {
        const networkCode = networkMapping.find((mapping: any) => mapping.countryCode === address?.TwoLetterISORegionName)?.networkCode;
        network = networkCode || 'postnord_DK';
    }
    if (address) {
        let orgUnitStoreInformation: IFullOrgUnitAvailabilityExtended[] = [];
        try {
            orgUnitStoreInformation = await getStoreLocationCentiroAction(
                new GetStoreLocationCentiroInput(
                    address?.TwoLetterISORegionName || '',
                    address?.City || '',
                    address?.ZipCode || '',
                    network,
                    address?.Street || '',
                    address?.State || '',
                    context.requestContext.app.config.collectionPointsRadius.toString() ?? '10',
                    context.requestContext.app.config.collectionPointsAmount.toString() ?? '5'
                ),
                context
            );
        } catch (error) {
            context.telemetry.error(
                `load-centiro-stores no stores were found | country: ${address?.TwoLetterISORegionName} | network: ${network} | address: ${address.FullAddress} | radius: ${context.requestContext.app.config.collectionPointsRadius} | amount: ${context.requestContext.app.config.collectionPointsAmount}`
            );
            return [];
        }
        if (!orgUnitStoreInformation || orgUnitStoreInformation.length === 0) {
            context.telemetry.error(
                `load-centiro-stores no stores were found | country: ${address?.TwoLetterISORegionName} | network: ${network} | address: ${address.FullAddress} | radius: ${context.requestContext.app.config.collectionPointsRadius} | amount: ${context.requestContext.app.config.collectionPointsAmount}`
            );
            return [];
        }
        setCentiroStores(
            orgUnitStoreInformation.map(item => {
                return {
                    City: item.OrgUnitAvailability?.OrgUnitLocation?.City,
                    Zip: item.OrgUnitAvailability?.OrgUnitLocation?.Zip,
                    Street: item.OrgUnitAvailability?.OrgUnitLocation?.Street,
                    OrgUnitName: item.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitName,
                    OrgUnitNumber: item.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber,
                    ExternalId: item.OrgUnitAvailability?.OrgUnitLocation?.ExternalId
                };
            })
        );
        return orgUnitStoreInformation;
    } else {
        return [];
    }
};

export const setCentiroStores = (stores: Object[]) => {
    _writeToSessionStorage(CENTIRO_STORES, JSON.stringify(stores), COLLECTION_POINT_EXPIRATION_DAYS);
};

export const getCentiroStores = (): Object[] | undefined => {
    const data = _readFromSessionStorage(CENTIRO_STORES);
    if (data) {
        return JSON.parse(data) as Object[];
    }
    return;
};

declare const window: IMapWindow;

interface IStoreSelectorLocationExtended extends IStoreSelectorLocation {
    address: {};
}

export const geoLocate = async (searchTerm: string, actionContext: IActionContext): Promise<IStoreSelectorLocationExtended | undefined> => {
    if (!searchTerm || !actionContext.requestContext.channel || !actionContext.requestContext.channel.AzureMapsApiKey) {
        return undefined;
    }

    let searchContent: string = searchTerm;

    if (actionContext.requestContext.channel.ChannelCountryRegionISOCode) {
        searchContent = `${searchTerm.trim()}, ${actionContext.requestContext.channel.ChannelCountryRegionISOCode}`;
    }

    try {
        const azureMapsSearchClient = createAzureMapsInstance(
            actionContext.requestContext.channel,
            actionContext.requestContext.locale,
            actionContext.telemetry
        );

        const response = await azureMapsSearchClient.searchAddress(searchContent);
        if (response.results && response.results.length > 0) {
            const result = response.results[0];
            return {
                address: result.address || {},
                latitude: result.position?.lat || 0,
                longitude: result.position?.lon || 0
            };
        }

        return undefined;
    } catch (error) {
        actionContext.telemetry.error('Error fetching location from Azure Maps:', error);
        return undefined;
    }
};
